/**
 * Le tableau de bord — « Vue d'ensemble ».
 *
 * Maquette : artboard `b_dashboard` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/dashboard-maquette.test.tsx` et
 * `test/app/approvals-maquette.test.tsx` **relisent** cette maquette et la
 * confrontent au livré, plutôt que d'en recopier les valeurs ici : un attendu
 * dérivé du code éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce que cet écran rendait, et ce qu'il rend
 *
 * Le dessin porte **neuf cartes**. Cet écran en rendait **une** — la double
 * validation — et déclarait les huit autres dans un paragraphe en tête de page.
 * Quatre sont désormais servies, et rendues :
 *
 *  1. **Contrôle de cantonnement** (`EX-REC-004`) — `GET /reconciliation/reports/{date}` ;
 *  5. **Files de travail** — trois de ses cinq lignes sont comptées ;
 *  7. **Double validation en attente** (`EX-BO-003`) — `GET /approvals` ;
 *  8. **Journal d'audit — inaltérable** — `GET /audit/entries`, qui s'ouvre sur
 *     la journée quand on ne lui donne ni borne ni filtre. C'est exactement
 *     l'aperçu que le dessin met là.
 *
 * Les cinq autres — volumes, revenu par ligne de produit, coût de distribution,
 * liquidité du réseau, activation des produits — et les **six indicateurs** de
 * la bande supérieure n'ont aucune route, et ne sont pas rendues.
 *
 * ## La déclaration a quitté la page, et la garde l'a suivie
 *
 * Le porteur a fait retirer du journal d'audit le pavé qui énumérait les
 * omissions : il encombre l'écran de qui travaille. La même décision vaut ici.
 * **Ce qui n'est pas rendu est déclaré sur
 * `_bmad-output/implementation-artifacts/deferred-work.md`**, et
 * `dashboard-maquette.test.tsx` l'y confronte aux routes *réellement servies* :
 * le jour où une carte devient constructible, le cas rougit. Sans cette garde,
 * une déclaration honnête se périme en silence — ce qui est arrivé deux fois en
 * une nuit sur la version précédente de cet écran.
 *
 * **Une exception, et elle est le sujet de cette carte** : ce qu'une carte
 * *rendue* ne mesure pas se dit **dans la carte, à la ligne**. Voir plus bas.
 *
 * ## Le piège de la carte à moitié servie
 *
 * « Files de travail » dessine quatre lignes ; deux seulement sont comptées par
 * une route — la file KYC et les écarts de réconciliation. Les alertes de
 * conformité (chantier 11) et les tickets hors SLA (chantier 13) ne le sont par
 * rien.
 *
 * **Les rendre à « 0 » serait le pire des deux mondes.** Un « 0 alerte de
 * conformité » se lit comme une bonne nouvelle, pas comme une absence de
 * mesure ; sur la carte qui sert à décider où l'on travaille aujourd'hui, cela
 * détournerait l'attention d'une file qui pourrait être pleine. Ces deux lignes
 * portent donc, à la place d'un compte, le **tiret de l'absence de mesure** et
 * la phrase qui le dit — et elles ne sont pas des boutons : elles ne mènent
 * nulle part, et un bouton qui ne fait rien apprend à ne plus cliquer.
 *
 * L'en-tête de la carte porte en plus le compte de ce qui est mesuré — « 3
 * files mesurées sur 5 » —, pour que l'absence se lise **avant** les chiffres
 * et non après.
 *
 * ## La quarantaine des commissions entre comme CINQUIÈME file
 *
 * *Décision du porteur, prise le 2026-09-10 après vérification que la maquette
 * ne la dessine nulle part.*
 *
 * `GET /commissions/quarantine` sert une file de commissions **retenues, qui
 * attendent une décision humaine** avant tout versement (`EX-TAR-014`). Aucun
 * écran ne l'appelait. Le mot « quarantaine » n'apparaît pas une fois dans la
 * maquette : lui bâtir une page entière reviendrait à **inventer une mise en
 * page**, ce que ce dépôt refuse au même titre qu'une valeur inventée. Elle
 * entre donc dans la carte dont la forme est dessinée et dont les quatre lignes
 * sont exactement de même nature — une file, son plus ancien, son compte.
 *
 * Deux conséquences, qui ne se renégocient pas :
 *
 *  - **le versement n'a pas d'écran à lui et n'en a pas besoin** : c'est une
 *    action à risque sous double validation, et sa place est la file de double
 *    validation déjà dessinée et déjà livrée **sur cet écran** ;
 *  - **la ligne ne dit pas le motif de détection.** Le contrat le sert —
 *    `reasonCode`, `reasonLabel`, `detail` — et le client HTTP le déclare,
 *    parce que le serveur le sert. L'écran du guichetier ne le révèle pas : le
 *    dire apprendrait à le contourner, et un écran de back-office qui
 *    l'afficherait en file, à côté de comptes, annulerait cette précaution. Le
 *    motif se lit **dans le dossier**, par qui décide.
 *
 * ## Chaque carte lit sa propre route, et échoue seule
 *
 * Cinq lectures partent à l'ouverture, et chacune peut être refusée
 * séparément : l'habilitation du back-office porte sur l'**action**, pas sur
 * l'écran (`EX-BO-002`), et les rôles ne tiennent pas les mêmes.
 * « Admin produit » — le rôle que la maquette illustre — n'a ni
 * `RECONCILIATION_VIEW`, ni `COMMISSION_VIEW`, ni `KYC_REVIEW_QUEUE_VIEW`, ni
 * `AUDIT_LOG_VIEW` : il ne verrait de cet écran que la double validation.
 *
 * Un refus sur une carte **n'efface donc pas les autres**, et il est dit dans
 * la carte refusée. Une page entière remplacée par « votre rôle n'a pas ce
 * droit » cacherait ce que le rôle a bel et bien le droit de voir.
 *
 * ## Ce que le dessin met et que le livré ne reprend pas
 *
 * Ces écarts-là sont **de forme**, non de contenu, et ils vivent ici plutôt que
 * sur `deferred-work.md`, qui porte les cartes entières :
 *
 *  1. **le compte de signatures** — « 1 / 2 validations ».
 *     `admin.approval_request` porte **un** demandeur et **un** approbateur :
 *     une demande en attente a, par construction, exactement une signature. La
 *     maquette écrit pourtant « 0 / 2 » sur l'une de ses trois lignes, état
 *     qu'aucune ligne de la table ne peut prendre. Afficher une fraction
 *     fabriquerait un état d'avancement ;
 *  2. **les noms d'affichage** — « A. Diop », « M. Ndiaye ». Le serveur rend
 *     des `UUID`, ici comme sur le journal d'audit ;
 *  3. **le bouton « Examiner »** navigue, dans la maquette, vers le tableau de
 *     bord lui-même : c'est un lien de démonstration. Ouvrir une ligne ouvre
 *     ici le panneau de décision, qui *fait* quelque chose ;
 *  4. **« Transactions orphelines », une seule valeur.** Le serveur en sert
 *     **deux** — internes et externes — et les fondre en une somme cacherait de
 *     quel côté l'orpheline se trouve, qui est justement ce qui décide de
 *     l'instruction. La carte rend les deux colonnes ;
 *  5. **le lien « Exporter »** de l'aperçu du journal. L'export existe, avec
 *     ses deux formes et son motif, sur l'écran du journal : le dupliquer ici
 *     ferait une seconde porte à tenir. L'en-tête porte un lien vers cet écran ;
 *  6. **l'ordre des deux dernières rangées.** Le dessin met la double
 *     validation au milieu d'une rangée de trois et l'aperçu du journal dans la
 *     dernière. Le livré de la double validation n'est pas l'aperçu de trois
 *     lignes du dessin : c'est une file complète, avec sa table, son panneau et
 *     son formulaire de motif — elle ne tient pas dans le tiers de rangée que
 *     le dessin lui donne. Les deux cartes **compactes** qui survivent à leurs
 *     rangées partagent donc une rangée, au rapport que l'artboard leur donne,
 *     et la file prend la rangée entière.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueEntry } from '../shell/QueueShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import type { ApiClient } from '../http/api-client.js';
import {
  approvePendingRequest,
  fetchPendingApprovals,
  queueAuditReason,
} from '../http/approvals.js';
import type { PendingApproval } from '../http/approvals.js';
import { fetchQuarantine } from '../http/commissions.js';
import type { QuarantinedCommission } from '../http/commissions.js';
import { fetchKycQueue } from '../http/kyc-review.js';
import type { KycQueueRow } from '../http/kyc-review.js';
import { fetchDiscrepancyQueue, fetchDailyReport } from '../http/reconciliation.js';
import type { DailyReport, Discrepancy } from '../http/reconciliation.js';
import { fetchNetworkLiquidity } from '../http/liquidity.js';
import { fetchFeatureFlags, settingsReason } from '../http/administration.js';
import type { FeatureFlag } from '../http/administration.js';
import type { NetworkLiquidity } from '../http/liquidity.js';
import { fetchTicketQueue } from '../http/support.js';
import type { SupportTicket } from '../http/support.js';
import { fetchAuditJournal } from '../http/audit-log.js';
import type { AuditPage } from '../http/audit-log.js';

/**
 * Les cinq colonnes de la file de double validation, dans l'ordre de la
 * lecture.
 *
 * Chacune est servie : `actionType`, `entityRef`, `requestedBy`, `requestedAt`,
 * `status`. La maquette groupe les deux premières sur une ligne et les deux
 * suivantes sur une autre ; la file les sépare parce que c'est ce qu'une file se
 * parcourt — une colonne se balaie du regard, une phrase se lit.
 */
export const approvalColumns: readonly QueueColumn[] = [
  { id: 'actionType', labelKey: 'approvals.columns.actionType' },
  { id: 'entityRef', labelKey: 'approvals.columns.entityRef' },
  { id: 'requestedBy', labelKey: 'approvals.columns.requestedBy' },
  { id: 'requestedAt', labelKey: 'approvals.columns.requestedAt' },
  { id: 'status', labelKey: 'approvals.columns.status' },
];

/**
 * Les quatre colonnes de l'aperçu du journal, dans l'ordre du dessin.
 *
 * Leurs **proportions** sont celles de l'artboard — `.5fr 1fr 1.6fr .8fr` — et
 * elles vivent dans la feuille de style, où `dashboard-maquette.test.tsx` va les
 * confronter à la chaîne `grid` du dessin.
 */
export const journalColumns: readonly string[] = [
  'dashboard.journal.columns.occurredAt',
  'dashboard.journal.columns.actor',
  'dashboard.journal.columns.action',
  'dashboard.journal.columns.ipAddress',
];

/**
 * Le nombre d'entrées que l'aperçu montre.
 *
 * C'est **le nombre de lignes que le dessin dessine**, et rien d'autre : la
 * route rend une page entière, et en montrer davantage ferait de l'aperçu un
 * second journal, en concurrence avec l'écran qui en est un.
 */
export const journalPreviewSize = 5;

/**
 * Le motif d'audit des lectures de la carte des files de travail.
 *
 * Une **constante** et non une saisie, pour la raison qui la rend constante
 * partout ailleurs : on ouvre toujours cette carte pour la même raison — savoir
 * où il faut travailler. Elle est distincte du motif de l'écran de revue : une
 * consultation qui ne lit qu'un COMPTE n'est pas l'ouverture d'un dossier, et
 * inscrire le même motif pour les deux rendrait le journal incapable de les
 * distinguer.
 */
const kycQueueAuditReason = 'files-de-travail-du-tableau-de-bord';

/** Le motif de la lecture de liquidité du tableau de bord. */
const liquidityAuditReason = 'liquidite-du-tableau-de-bord';

/**
 * Le seul champ de la décision d'approbation, et il est obligatoire.
 *
 * Le serveur exige `reason` (`BackOfficeService.requireReason`). Le rendre
 * facultatif ici ferait partir une requête que le serveur refuserait en `400`,
 * et l'agent lirait « le serveur a rencontré une erreur » là où il manque une
 * phrase de sa main.
 */
const champsDeDecision: readonly FormFieldDescriptor[] = [
  { name: 'reason', labelKey: 'approvals.fields.reason', required: true },
];

/**
 * L'état d'une lecture : ni lue, refusée, ou lue.
 *
 * Les trois sont distincts et le restent jusqu'au rendu. Replier « refusée » sur
 * « lue et vide » est la faute que ce dépôt a nommée cinq fois : sur une file,
 * un refus rendu comme un vide se lit « il n'y a rien à faire », c'est-à-dire
 * l'inverse de ce qui s'est passé.
 */
interface Lecture<T> {
  readonly valeur: T | null;
  readonly erreur: string | null;
}

const enAttente: Lecture<never> = { valeur: null, erreur: null };

/** La clé de message d'un échec, quelle que soit sa nature. */
function cleDEchec(echec: unknown): string {
  return echec instanceof ApiError ? echec.messageKey : 'errors.server';
}

/**
 * La journée comptable que la carte de cantonnement demande.
 *
 * `GET /reconciliation/reports/{date}` **exige une journée** : il n'existe
 * aucune route qui rende « le dernier contrôle ». Celle-ci est prise sur
 * l'horloge du poste, et la carte **écrit la journée qu'elle montre** plutôt
 * que de laisser croire qu'elle montre « maintenant » : un poste mal réglé, ou
 * un agent à cheval sur minuit, lirait sinon le rapport d'une autre journée
 * sans que rien le dise.
 */
export function journeeDuPoste(maintenant: Date = new Date()): string {
  const deuxChiffres = (nombre: number): string => String(nombre).padStart(2, '0');
  return [
    maintenant.getFullYear(),
    deuxChiffres(maintenant.getMonth() + 1),
    deuxChiffres(maintenant.getDate()),
  ].join('-');
}

/** L'écran : ce que l'exploitation doit regarder en ouvrant le back-office. */
export function DashboardScreen(): ReactNode {
  const journee = useMemo(() => journeeDuPoste(), []);

  return (
    <div className="dashboard">
      <CustodyCard businessDate={journee} />

      <div className="dashboard__row">
        <WorkQueuesCard />
        <JournalPreviewCard />
      </div>

      <div className="dashboard__row">
        <NetworkLiquidityCard />
        <ProductActivationCard />
      </div>

      <ApprovalsQueue />
    </div>
  );
}

// --- 1. Contrôle de cantonnement · EX-REC-004 -------------------------------

/**
 * Le contrôle de cantonnement de la journée.
 *
 * **`controlled` et `reconciled` sont rendus avant les chiffres**, et ce n'est
 * pas décoratif : le contrat les sert précisément parce qu'une ligne absente ne
 * dit rien. Une journée peut avoir été cantonnée sans avoir été rapprochée, et
 * l'inverse. Rendre « écart 0 » sur une journée non contrôlée annoncerait un
 * équilibre que personne n'a mesuré, sur la carte qui existe pour dire que
 * l'argent des clients est là.
 */
function CustodyCard({ businessDate }: { readonly businessDate: string }): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();
  const [lecture, setLecture] = useState<Lecture<DailyReport>>(enAttente);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const rapport = await fetchDailyReport(client, businessDate);
        if (vivant) setLecture({ valeur: rapport, erreur: null });
      } catch (echec) {
        if (vivant) setLecture({ valeur: null, erreur: cleDEchec(echec) });
      }
    })();
    return () => {
      vivant = false;
    };
  }, [businessDate, client]);

  /**
   * Un montant servi, groupé dans la langue de l'agent — **sans devise**.
   *
   * Aucun des contrats du rapprochement n'en sert une. `style: 'currency'`
   * exigerait d'en choisir une ici ; l'en-tête de la carte dit à la place qu'il
   * s'agit d'unités mineures.
   */
  const montant = useCallback(
    (mineures: number): string => new Intl.NumberFormat(i18n.language).format(mineures),
    [i18n.language],
  );

  /**
   * L'écart, **avec son signe**, y compris quand il est nul.
   *
   * Le signe est porteur : positif, les clients détiennent plus que la banque
   * ne couvre — c'est l'incident ; négatif, le cantonnement sur-couvre, ce qui
   * est l'état normal après un prélèvement de frais. La valeur absolue
   * alarmerait à chaque frais.
   */
  const ecartSigne = useCallback(
    (mineures: number): string =>
      new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero' }).format(mineures),
    [i18n.language],
  );

  const heure = useCallback(
    (valeur: string): string =>
      new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }).format(new Date(valeur)),
    [i18n.language],
  );

  const rapport = lecture.valeur;

  return (
    <section className="card card--custody" aria-label={t('dashboard.custody.title')}>
      <header className="card__header">
        {/* Le dessin empile le sur-titre et sa note dans un même bloc de deux
            lignes — « Contrôle de cantonnement · EX-REC-004 » au-dessus de
            « Dernier rapprochement automatique à 06:00 ». Les poser côte à côte
            faisait une ligne qui court et que rien ne hiérarchise. */}
        <span className="card__identity">
          <span className="card__notes">
            <span className="card__eyebrow">{t('dashboard.custody.title')}</span>
            <span className="card__requirement">{t('dashboard.custody.requirement')}</span>
          </span>
          <span className="card__notes">
            <span className="card__note">
              {t('dashboard.custody.businessDate', { date: businessDate })}
            </span>
            {rapport?.custody == null ? null : (
              <span className="card__note" data-testid="custody-computed-at">
                {t('dashboard.custody.computedAt', { time: heure(rapport.custody.computedAt) })}
              </span>
            )}
            {rapport?.reconciliation == null ? null : (
              <span className="card__note" data-testid="custody-ran-at">
                {t('dashboard.custody.ranAt', { time: heure(rapport.reconciliation.ranAt) })}
              </span>
            )}
          </span>
        </span>
        <Link className="card__action" to="/finance">
          {t('dashboard.custody.report')}
        </Link>
      </header>

      {lecture.erreur !== null ? (
        <p className="card__notice" role="alert" data-testid="custody-error">
          {t(lecture.erreur)}
        </p>
      ) : rapport === null ? (
        <p className="card__idle">{t('dashboard.custody.loading')}</p>
      ) : (
        <div className="card__body card__body--custody">
          {/* **Aucune mesure n'est pas un écart nul.** Le contrat sert
              `controlled` exactement pour permettre de le dire. */}
          {rapport.controlled && rapport.custody !== null ? (
            <>
              <CustodyFigure
                label={t('dashboard.custody.holders')}
                value={montant(rapport.custody.holdersBalance)}
                testId="custody-holders"
              />
              <CustodyFigure
                label={t('dashboard.custody.trust')}
                value={montant(rapport.custody.trustBalance)}
                testId="custody-trust"
              />
              <CustodyFigure
                label={t('dashboard.custody.gap')}
                value={ecartSigne(rapport.custody.gap)}
                tone={rapport.custody.gap === 0 ? 'success' : 'danger'}
                testId="custody-gap"
              />
            </>
          ) : (
            <p className="card__gap" data-testid="custody-not-controlled">
              {t('dashboard.custody.notControlled')}
            </p>
          )}

          {/* Les orphelines sont servies par le RAPPROCHEMENT, pas par le
              contrôle : les deux tournent séparément, et la carte le dit. */}
          {rapport.reconciled && rapport.reconciliation !== null ? (
            <>
              <CustodyFigure
                label={t('dashboard.custody.internalOrphans')}
                value={String(rapport.reconciliation.internalOrphanCount)}
                tone={rapport.reconciliation.internalOrphanCount === 0 ? 'success' : 'warning'}
                testId="custody-internal-orphans"
              />
              <CustodyFigure
                label={t('dashboard.custody.externalOrphans')}
                value={String(rapport.reconciliation.externalOrphanCount)}
                tone={rapport.reconciliation.externalOrphanCount === 0 ? 'success' : 'warning'}
                testId="custody-external-orphans"
              />
            </>
          ) : (
            <p className="card__gap" data-testid="custody-not-reconciled">
              {t('dashboard.custody.notReconciled')}
            </p>
          )}

          <span className="card__figures-end">
            <span
              className={
                rapport.reconciled ? 'card__badge card__badge--success' : 'card__badge'
              }
              data-testid="custody-reconciled"
            >
              {t(
                rapport.reconciled
                  ? 'dashboard.custody.reconciled'
                  : 'dashboard.custody.notReconciledBadge',
              )}
            </span>
            {rapport.custody === null ? null : (
              <span className="card__badge" data-testid="custody-severity">
                {t('dashboard.custody.severity', { code: rapport.custody.severity })}
              </span>
            )}
            {rapport.custody?.blocked === true ? (
              <span className="card__badge card__badge--danger" data-testid="custody-blocked">
                {t('dashboard.custody.blocked')}
              </span>
            ) : null}
          </span>
        </div>
      )}

      <p className="card__footnote">{t('dashboard.custody.minorUnits')}</p>
    </section>
  );
}

/**
 * Une valeur du bandeau de cantonnement : son libellé, puis son chiffre.
 *
 * Le dessin les sépare par un filet à gauche et pose le libellé en 11 points
 * au-dessus d'un chiffre de 16 en graisse 600. Les 11 points remontent au
 * plancher de lecture — voir la feuille de style —, et la hiérarchie tient
 * alors par la graisse et la couleur.
 */
function CustodyFigure({
  label,
  value,
  tone,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'success' | 'warning' | 'danger';
  readonly testId: string;
}): ReactNode {
  return (
    <span className="card__figure">
      <span className="card__figure-label">{label}</span>
      <span className="card__figure-value" data-tone={tone ?? 'plain'} data-testid={testId}>
        {value}
      </span>
    </span>
  );
}

// --- 5. Files de travail ----------------------------------------------------

/** Ce qu'une ligne de la carte porte, servie ou non. */
interface LigneDeFile {
  readonly id: string;
  readonly labelKey: string;
  /** Le compte servi, ou `null` quand **rien ne compte cette file**. */
  readonly compte: number | null;
  /** Ce que la ligne dit sous son libellé. */
  readonly meta: string;
  /** La destination, ou `null` : une ligne sans destination n'est pas un bouton. */
  readonly to: string | null;
  readonly tone: 'success' | 'warning' | 'danger' | 'absent';
  /** La clé du message d'échec, quand la lecture a été refusée. */
  readonly erreur: string | null;
}

/**
 * Les cinq files de travail — trois comptées, deux pas.
 *
 * Voir l'en-tête du fichier : c'est la carte la plus trompeuse du dessin, et
 * c'est ici que se joue la seule chose qui compte sur un tableau de bord — que
 * l'absence de mesure ne se lise pas comme une bonne nouvelle.
 */
function WorkQueuesCard(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();

  const [kyc, setKyc] = useState<Lecture<readonly KycQueueRow[]>>(enAttente);
  const [ecarts, setEcarts] = useState<Lecture<readonly Discrepancy[]>>(enAttente);
  const [quarantaine, setQuarantaine] = useState<Lecture<readonly QuarantinedCommission[]>>(
    enAttente,
  );
  const [support, setSupport] = useState<Lecture<readonly SupportTicket[]>>(enAttente);

  useEffect(() => {
    let vivant = true;
    const lire = async <T,>(
      charger: (client: ApiClient) => Promise<T>,
      poser: (lecture: Lecture<T>) => void,
    ): Promise<void> => {
      try {
        const valeur = await charger(client);
        if (vivant) poser({ valeur, erreur: null });
      } catch (echec) {
        if (vivant) poser({ valeur: null, erreur: cleDEchec(echec) });
      }
    };
    void lire(async (api) => {
      const file = await fetchKycQueue(api, kycQueueAuditReason);
      // La route de la file KYC ne vérifie pas sa forme dans le client HTTP —
      // son écran la lit champ par champ. Ici, seul le COMPTE est lu : une
      // réponse d'une autre forme rendrait `undefined.length` et laisserait la
      // carte blanche, c'est-à-dire pire que muette.
      if (!Array.isArray(file?.rows)) throw new Error('forme');
      return file.rows;
    }, setKyc);
    void lire((api) => fetchDiscrepancyQueue(api), setEcarts);
    void lire((api) => fetchQuarantine(api), setQuarantaine);
    // **La file support CESSE d'être « aucune mesure ».** Elle l'était parce
    // qu'aucun écran n'appelait `GET /support/tickets` ; la route était servie
    // et éprouvée depuis le chantier 13. C'est le contrôle des omissions qui
    // l'a dit : une déclaration honnête se périme en silence, et celle-ci a
    // cessé d'être vraie le jour où le module Support a été livré.
    void lire(async (api) => {
      const tickets = await fetchTicketQueue(api, kycQueueAuditReason);
      // La même garde que la file KYC, et pour la même raison : seul le COMPTE
      // est lu ici. Une réponse d'une autre forme rendrait `undefined.filter` et
      // laisserait la carte BLANCHE, c'est-à-dire pire que muette.
      if (!Array.isArray(tickets)) throw new Error('forme');
      return tickets;
    }, setSupport);
    return () => {
      vivant = false;
    };
  }, [client]);

  const instant = useCallback(
    (valeur: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(valeur)),
    [i18n.language],
  );

  /**
   * La durée que le **serveur** a mesurée, mise en mots.
   *
   * Seule la file KYC sert une ancienneté (`ageSeconds`, mesurée côté serveur).
   * Les deux autres servent l'instant d'ouverture, et la carte le rend **tel
   * quel** plutôt que de le soustraire à l'horloge du poste : une horloge
   * décalée transformerait une file fraîche en file en retard, ou l'inverse.
   */
  const duree = useCallback(
    (secondes: number): string => {
      const minutes = Math.floor(secondes / 60);
      if (minutes < 60) return t('dashboard.queues.minutes', { count: minutes });
      return t('dashboard.queues.hours', {
        hours: Math.floor(minutes / 60),
        minutes: String(minutes % 60).padStart(2, '0'),
      });
    },
    [t],
  );

  const lignes: readonly LigneDeFile[] = useMemo(() => {
    const kycRows = kyc.valeur;
    const plusAncienKyc =
      kycRows === null || kycRows.length === 0
        ? null
        : kycRows.reduce((age, ligne) => Math.max(age, ligne.ageSeconds), 0);
    const plusAncienEcart =
      ecarts.valeur === null || ecarts.valeur.length === 0
        ? null
        : ecarts.valeur.reduce(
            (ancien, ecart) => (ecart.openedAt < ancien ? ecart.openedAt : ancien),
            ecarts.valeur[0].openedAt,
          );
    const plusAncienneRetenue =
      quarantaine.valeur === null || quarantaine.valeur.length === 0
        ? null
        : quarantaine.valeur.reduce(
            (ancien, retenue) => (retenue.detectedAt < ancien ? retenue.detectedAt : ancien),
            quarantaine.valeur[0].detectedAt,
          );

    // L'échéance est un instant SERVI. Une date illisible n'est PAS un
    // dépassement : annoncer « hors échéance » sur une valeur qu'on n'a pas su
    // lire serait une alarme fabriquée.
    const horsEcheance =
      support.valeur === null
        ? null
        : support.valeur.filter((ticket) => {
            if (ticket.slaDueAt === null) return false;
            const echeance = Date.parse(ticket.slaDueAt);
            return !Number.isNaN(echeance) && echeance < Date.now();
          }).length;

    return [
      {
        id: 'kyc',
        labelKey: 'dashboard.queues.kyc',
        compte: kycRows?.length ?? null,
        meta:
          plusAncienKyc === null
            ? t('dashboard.queues.noneWaiting')
            : t('dashboard.queues.oldestAge', { age: duree(plusAncienKyc) }),
        to: '/identity-review',
        tone: (kycRows?.length ?? 0) > 0 ? 'warning' : 'success',
        erreur: kyc.erreur,
      },
      {
        // Chantier 11. Rien ne compte cette file : elle ne porte donc AUCUN
        // chiffre. Voir l'en-tête du fichier.
        id: 'compliance',
        labelKey: 'dashboard.queues.compliance',
        compte: null,
        meta: t('dashboard.queues.notMeasured'),
        to: null,
        tone: 'absent',
        erreur: null,
      },
      {
        id: 'discrepancies',
        labelKey: 'dashboard.queues.discrepancies',
        compte: ecarts.valeur?.length ?? null,
        meta:
          plusAncienEcart === null
            ? t('dashboard.queues.noneWaiting')
            : t('dashboard.queues.oldestAt', { instant: instant(plusAncienEcart) }),
        to: '/finance',
        tone: (ecarts.valeur?.length ?? 0) > 0 ? 'warning' : 'success',
        erreur: ecarts.erreur,
      },
      {
        // **Les tickets HORS ÉCHÉANCE, et non tous les tickets.** Le libellé de
        // la carte dit « hors SLA » ; y mettre la taille de la file entière
        // rendrait un chiffre juste sous un titre faux, ce qui est pire qu'un
        // tiret. L'échéance est SERVIE — `slaDueAt` — et comparée à maintenant :
        // l'écran n'en recalcule aucune.
        id: 'support',
        labelKey: 'dashboard.queues.support',
        compte: horsEcheance,
        meta:
          horsEcheance === null
            ? t('dashboard.queues.notMeasured')
            : horsEcheance === 0
              ? t('dashboard.queues.noneWaiting')
              : t('dashboard.queues.lateCount', { count: support.valeur?.length ?? 0 }),
        to: '/support',
        tone: horsEcheance === null ? 'absent' : horsEcheance > 0 ? 'warning' : 'success',
        erreur: support.erreur,
      },
      {
        // La cinquième file, entrée par décision du porteur. **Son méta ne dit
        // jamais le motif de détection** : ni `reasonCode`, ni `reasonLabel`,
        // ni `detail`. Il dit quand la plus ancienne a été retenue, et où la
        // levée se décide.
        id: 'quarantine',
        labelKey: 'dashboard.queues.quarantine',
        compte: quarantaine.valeur?.length ?? null,
        meta:
          plusAncienneRetenue === null
            ? t('dashboard.queues.noneWaiting')
            : t('dashboard.queues.oldestAt', { instant: instant(plusAncienneRetenue) }),
        to: null,
        tone: (quarantaine.valeur?.length ?? 0) > 0 ? 'danger' : 'success',
        erreur: quarantaine.erreur,
      },
    ];
  }, [duree, ecarts, instant, kyc, quarantaine, t]);

  /**
   * Combien de ces files une route compte réellement.
   *
   * Le compte est **dérivé** des lignes, jamais écrit : une sixième file qui
   * entrerait sans route le ferait bouger toute seule, et une route qui
   * apparaîtrait aussi.
   */
  const mesurees = lignes.filter((ligne) => ligne.tone !== 'absent').length;

  return (
    <section className="card card--queues" aria-label={t('dashboard.queues.title')}>
      <header className="card__header card__header--titled">
        <h2 className="card__title">{t('dashboard.queues.title')}</h2>
        <span className="card__note" data-testid="queues-measured">
          {t('dashboard.queues.measured', { measured: mesurees, total: lignes.length })}
        </span>
      </header>

      <ul className="queues">
        {lignes.map((ligne) => (
          <li className="queues__item" key={ligne.id}>
            <WorkQueueRow line={ligne} />
          </li>
        ))}
      </ul>

      <p className="card__footnote">{t('dashboard.queues.quarantineNote')}</p>
    </section>
  );
}

/**
 * Une ligne de la carte des files.
 *
 * Elle est un **lien** quand elle mène quelque part, et un simple bloc sinon.
 * Le dessin fait de ses quatre lignes des boutons, mais trois de ses quatre
 * destinations sont le tableau de bord lui-même — des liens de démonstration.
 * Un bouton qui ne fait rien apprend à ne plus cliquer.
 */
function WorkQueueRow({ line }: { readonly line: LigneDeFile }): ReactNode {
  const { t } = useTranslation();

  const corps = (
    <>
      <span className="queues__dot" data-tone={line.tone} />
      <span className="queues__identity">
        <span className="queues__label">{t(line.labelKey)}</span>
        <span className="queues__meta">{line.erreur === null ? line.meta : t(line.erreur)}</span>
      </span>
      <span
        className="queues__value"
        data-measured={line.compte !== null}
        data-testid={`queue-value-${line.id}`}
      >
        {line.erreur !== null
          ? t('dashboard.queues.refusedValue')
          : line.compte === null
            ? t('dashboard.queues.notMeasuredValue')
            : String(line.compte)}
      </span>
    </>
  );

  if (line.to === null || line.erreur !== null) {
    return (
      <div className="queues__row" data-testid={`queue-row-${line.id}`}>
        {corps}
      </div>
    );
  }
  return (
    <Link
      className="queues__row queues__row--link"
      to={line.to}
      data-testid={`queue-row-${line.id}`}
    >
      {corps}
    </Link>
  );
}

// --- 7 bis. Liquidité du réseau · EX-LIQ-003 --------------------------------

/**
 * Les guichets en tension — la carte que la maquette dessine, enfin remplie.
 *
 * **Elle était omise entière, et le manque était déclaré.** La raison écrite
 * dans `deferred-work.md` était juste : « aucune route ne rend le plafond
 * d'encaisse d'un guichet, ni son encours, ni un ratio, ni un compte d'échecs
 * par cause », et « une carte de liquidité à moitié remplie serait pire
 * qu'absente : c'est celle qui décide d'envoyer une collecte, et un guichet
 * manquant s'y lirait comme un guichet sain ».
 *
 * Cette raison a cessé d'être vraie le jour où le module Réseau guichets a
 * livré son écran : `GET /liquidity/dashboard` était servie depuis le chantier
 * 8 et appelée par personne. **C'est le contrôle des omissions qui l'a dit**,
 * en rougissant à la livraison, exactement comme son en-tête annonce qu'il le
 * ferait.
 *
 * La carte ne montre donc PAS un ratio inventé : elle liste les guichets que le
 * serveur déclare en tension, avec ce qu'il en dit — et rien d'autre.
 */
function NetworkLiquidityCard(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const [lecture, setLecture] = useState<Lecture<NetworkLiquidity>>(enAttente);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const vue = await fetchNetworkLiquidity(client, liquidityAuditReason);
        if (vivant) setLecture({ valeur: vue, erreur: null });
      } catch (echec) {
        if (vivant) setLecture({ valeur: null, erreur: cleDEchec(echec) });
      }
    })();
    return () => {
      vivant = false;
    };
  }, [client]);

  const tendus = useMemo(
    () => (lecture.valeur?.positions ?? []).filter((ligne) => ligne.underTension),
    [lecture.valeur],
  );

  return (
    <section aria-label={t('dashboard.liquidity.title')} className="card card--liquidity">
      <header className="card__header">
        <h2 className="card__title">{t('dashboard.liquidity.title')}</h2>
        <Link className="card__action" to="/outlet-network">
          {t('dashboard.liquidity.open')}
        </Link>
      </header>

      {lecture.erreur !== null ? (
        <p className="card__notice" role="alert">
          {t(lecture.erreur)}
        </p>
      ) : lecture.valeur === null ? (
        <p className="card__idle">{t('dashboard.liquidity.loading')}</p>
      ) : (
        <>
          <p className="card__figure" data-testid="liquidity-tension">
            {t('dashboard.liquidity.tension', {
              tense: lecture.valeur.outletsUnderTension,
              total: lecture.valeur.outlets,
            })}
          </p>

          {/* **Le taux ne se rend jamais seul.** `rateIsMeasured` dit si la
              mesure porte sur quelque chose : 100 % sur zéro opération est vrai
              et ne démontre rien. C'est la somme nulle qui a laissé `D-53`
              vivre. */}
          <p className="card__footnote" data-testid="liquidity-rate">
            {lecture.valeur.rateIsMeasured
              ? t('dashboard.liquidity.rate', {
                  value: lecture.valeur.serviceRatePercent.toFixed(1),
                  served: lecture.valeur.servedOperations,
                  days: lecture.valeur.windowDays,
                })
              : t('dashboard.liquidity.rateNotMeasured', {
                  days: lecture.valeur.windowDays,
                })}
          </p>

          {tendus.length === 0 ? (
            <p className="card__idle" data-testid="liquidity-none">
              {t('dashboard.liquidity.none')}
            </p>
          ) : (
            <ul className="card__list" data-testid="liquidity-list">
              {tendus.map((ligne) => (
                <li className="card__list-item" key={ligne.outletId}>
                  <span className="card__list-name">
                    {ligne.outletCode} — {ligne.businessName}
                  </span>
                  <span className="card__list-meta">
                    {/* Une autonomie INCONNUE se dit : elle n'est pas nulle, et
                        elle n'est pas rassurante. */}
                    {ligne.daysOfAutonomy === null
                      ? t('dashboard.liquidity.unknownAutonomy')
                      : t('dashboard.liquidity.autonomy', { count: ligne.daysOfAutonomy })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/**
 * L'activation des produits : ce qui est ouvert, et **à quelle part du parc**.
 *
 * **Troisième carte que le contrôle des omissions a exigée dans ce chantier**,
 * après le support et la liquidité, et pour la même raison :
 * `GET /backoffice/feature-flags` est désormais appelée, donc déclarer cette
 * carte « absente » serait devenu faux — et une déclaration fausse a
 * l'apparence de l'honnêteté, ce qui la rend pire qu'un silence.
 *
 * **La part accompagne toujours l'état.** Un produit « ouvert » à 3 % n'est pas
 * un produit ouvert, et compter dix produits ouverts sans regarder leur
 * fraction annoncerait une mise en service qui n'a pas eu lieu. Les produits
 * ouverts à une fraction sont donc énumérés, un par un ; les autres sont
 * comptés.
 */
function ProductActivationCard(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const [lecture, setLecture] = useState<Lecture<readonly FeatureFlag[]>>(enAttente);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const vue = await fetchFeatureFlags(client, settingsReason);
        if (vivant) setLecture({ valeur: vue, erreur: null });
      } catch (echec) {
        if (vivant) setLecture({ valeur: null, erreur: cleDEchec(echec) });
      }
    })();
    return () => {
      vivant = false;
    };
  }, [client]);

  /** Les produits ouverts à une FRACTION du parc : ceux qu'il faut nommer. */
  const partiels = useMemo(
    () =>
      (lecture.valeur ?? []).filter(
        (drapeau) => drapeau.enabled && drapeau.rolloutPercentage < 100,
      ),
    [lecture.valeur],
  );

  const ouverts = (lecture.valeur ?? []).filter((drapeau) => drapeau.enabled).length;

  return (
    <section aria-label={t('dashboard.products.title')} className="card">
      <header className="card__header">
        <h2 className="card__title">{t('dashboard.products.title')}</h2>
        <Link className="card__action" to="/system-settings">
          {t('dashboard.products.open')}
        </Link>
      </header>

      {lecture.erreur !== null ? (
        <p className="card__notice" role="alert">
          {t(lecture.erreur)}
        </p>
      ) : lecture.valeur === null ? (
        <p className="card__idle">{t('dashboard.products.loading')}</p>
      ) : (
        <>
          <p className="card__figure" data-testid="products-open">
            {t('dashboard.products.open_count', {
              opened: ouverts,
              total: lecture.valeur.length,
            })}
          </p>

          {/* **Une ouverture partielle se NOMME.** La compter avec les autres
              ferait lire « dix produits ouverts » là où l'un d'eux ne touche
              que trois clients sur cent. */}
          {partiels.length === 0 ? (
            <p className="card__footnote" data-testid="products-none-partial">
              {t('dashboard.products.nonePartial')}
            </p>
          ) : (
            <ul className="card__list" data-testid="products-partial">
              {partiels.map((drapeau) => (
                <li className="card__list-item" key={drapeau.code}>
                  <span className="card__list-name">{drapeau.description}</span>
                  <span className="card__list-meta">
                    {t('dashboard.products.partial', {
                      rollout: drapeau.rolloutPercentage,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// --- 9. Journal d'audit — inaltérable ---------------------------------------

/**
 * L'aperçu du journal : les entrées **du jour**, cinq au plus.
 *
 * `GET /audit/entries` sans borne ni filtre s'ouvre sur la journée courante —
 * c'est le contrat, pas une supposition de l'écran : la page rend `from` et
 * `to` effectivement appliqués, et l'aperçu les affiche.
 *
 * **Consulter le journal est soi-même un acte journalisé.** Ouvrir le tableau
 * de bord écrit donc une consultation, au même titre qu'ouvrir l'écran du
 * journal. C'est ce que le socle a voulu en ajoutant `ENTITY_AUDIT_LOG`, et ce
 * n'est pas un effet de bord à cacher.
 */
function JournalPreviewCard(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();
  const [lecture, setLecture] = useState<Lecture<AuditPage>>(enAttente);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const page = await fetchAuditJournal(client);
        if (vivant) setLecture({ valeur: page, erreur: null });
      } catch (echec) {
        if (vivant) setLecture({ valeur: null, erreur: cleDEchec(echec) });
      }
    })();
    return () => {
      vivant = false;
    };
  }, [client]);

  const heure = useCallback(
    (valeur: string): string =>
      new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }).format(new Date(valeur)),
    [i18n.language],
  );

  const page = lecture.valeur;
  const apercu = (page?.entries ?? []).slice(0, journalPreviewSize);

  return (
    <section className="card card--journal" aria-label={t('dashboard.journal.title')}>
      <header className="card__header card__header--titled">
        <h2 className="card__title">{t('dashboard.journal.title')}</h2>
        <Link className="card__action card__action--quiet" to="/audit-log">
          {t('dashboard.journal.openFull')}
        </Link>
      </header>

      {lecture.erreur !== null ? (
        <p className="card__notice" role="alert" data-testid="journal-error">
          {t(lecture.erreur)}
        </p>
      ) : page === null ? (
        <p className="card__idle">{t('dashboard.journal.loading')}</p>
      ) : apercu.length === 0 ? (
        <p className="card__gap" data-testid="journal-empty">
          {t('dashboard.journal.empty')}
        </p>
      ) : (
        <>
          <div className="journal" role="table" aria-label={t('dashboard.journal.title')}>
            <div className="journal__head" role="row">
              {journalColumns.map((cle) => (
                <span className="journal__heading" role="columnheader" key={cle}>
                  {t(cle)}
                </span>
              ))}
            </div>
            {apercu.map((entree) => (
              <div className="journal__row" role="row" key={entree.id}>
                <span className="journal__cell" role="cell" data-emphasis="numeric">
                  {heure(entree.occurredAt)}
                </span>
                {/* Le serveur rend un identifiant, jamais un nom d'affichage.
                    La maquette écrit « A. Diop » ; l'inventer mentirait sur qui
                    a agi, dans l'aperçu du seul journal qui fasse foi. */}
                <span className="journal__cell" role="cell" data-emphasis="strong">
                  {entree.actorId === null
                    ? t('auditLog.actorWithoutId', { type: entree.actorType })
                    : t('auditLog.actor', { type: entree.actorType, id: entree.actorId })}
                </span>
                <span className="journal__cell" role="cell" data-emphasis="muted">
                  {entree.action}
                </span>
                <span className="journal__cell" role="cell" data-emphasis="muted">
                  {entree.ipAddress ?? t('auditLog.ipMissing')}
                </span>
              </div>
            ))}
          </div>
          <p className="card__footnote" data-testid="journal-count">
            {t('dashboard.journal.count', {
              shown: apercu.length,
              total: page.total,
              from: page.from,
              to: page.to,
            })}
          </p>
        </>
      )}
    </section>
  );
}

// --- 7. Double validation en attente · EX-BO-003 ----------------------------

/**
 * La file de double validation, telle qu'elle était livrée — et qui ne bouge
 * pas.
 *
 * Le socle savait mettre une action à risque **en attente**
 * (`POST /actions/{action}` → `202`) et savait la **décider**
 * (`POST /approvals/{id}/approval`). Il ne savait pas dire **ce qui attend** :
 * une seconde signature qu'on ne peut pas trouver est une seconde signature qui
 * n'existe pas. C'est ce que cette file a refermé, et c'est pourquoi elle vit
 * sur l'écran d'accueil du back-office.
 *
 * **L'approbation exige un motif écrit, et ne peut pas être un raccourci.**
 * `QueueShell` sait rendre une décision d'une seule touche, et la revue KYC s'en
 * sert. Pas ici : `requireReason` refuse un motif vide, et une touche unique
 * signerait une action à risque sans que personne ait écrit pourquoi. Le motif
 * *de la consultation* et le motif *de la décision* sont deux choses : le
 * premier est constant, le second est écrit demande par demande, et c'est lui
 * qui rejoint les deux identités dans le journal.
 */
export function ApprovalsQueue(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [attente, setAttente] = useState<readonly PendingApproval[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setAttente(await fetchPendingApprovals(client, queueAuditReason));
      setErreur(null);
    } catch (echec) {
      // La file lue est effacée avec l'échec. La garder à l'écran laisserait
      // croire qu'elle est à jour, et un approbateur signerait d'après une liste
      // que le serveur a cessé de confirmer.
      setAttente(null);
      setErreur(cleDEchec(echec));
    }
  }, [client]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const approuver = useCallback(
    async (requestId: string, motif: string) => {
      try {
        await approvePendingRequest(client, requestId, motif);
        setErreur(null);
        await charger();
      } catch (echec) {
        // Un refus d'habilitation et un refus d'auto-approbation arrivent tous
        // deux en 403 : le serveur ne dit pas lequel, et l'écran ne l'invente
        // pas. Il dit que la signature a été refusée.
        setErreur(cleDEchec(echec));
      }
    },
    [charger, client],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (attente ?? []).map((demande) => ({
        id: demande.id,
        reference: demande.id,
        values: [
          demande.actionType,
          demande.entityRef,
          demande.requestedBy,
          demande.requestedAt,
          demande.status,
        ],
      })),
    [attente],
  );

  return (
    <section className="approvals" aria-label={t('approvals.landmark')}>
      <header className="approvals__header">
        <h2 className="approvals__title">{t('approvals.heading')}</h2>
        {/* L'exigence est nommée sur la carte, comme dans la maquette : un
            approbateur doit pouvoir dire de quelle règle il tient sa signature. */}
        <span className="approvals__requirement">{t('approvals.requirement')}</span>
      </header>

      {erreur === null ? null : (
        <p className="approvals__notice" role="alert">
          {t(erreur)}
        </p>
      )}

      {attente === null ? (
        <p className="approvals__idle">{t('approvals.loading')}</p>
      ) : (
        <>
          <p className="approvals__result" data-testid="approvals-result">
            {t('approvals.result', { count: attente.length })}
          </p>
          <QueueShell
            columns={approvalColumns}
            entries={lignes}
            renderDetail={(ligne) => (
              <ApprovalDetail
                request={attente.find((candidate) => candidate.id === ligne.id) ?? null}
                onApprove={approuver}
              />
            )}
          />
        </>
      )}
    </section>
  );
}

/**
 * Le panneau d'une demande : ce qu'elle exécutera, qui l'a demandée et pourquoi,
 * puis le motif de la décision.
 *
 * Le motif **du demandeur** est montré avant le champ, et c'est ce qui distingue
 * une seconde signature d'un second clic : le signataire lit l'argument de celui
 * qui demande avant d'écrire le sien.
 */
function ApprovalDetail({
  request,
  onApprove,
}: {
  readonly request: PendingApproval | null;
  readonly onApprove: (requestId: string, reason: string) => void | Promise<void>;
}): ReactNode {
  const { t } = useTranslation();
  if (request === null) {
    return <p className="approvals__idle">{t('approvals.requestMissing')}</p>;
  }
  return (
    <div className="approvals__request">
      <dl className="approvals__facts">
        <dt>{t('approvals.detail.action')}</dt>
        <dd data-testid="approval-detail-action">{request.actionType}</dd>
        <dt>{t('approvals.detail.target')}</dt>
        <dd data-testid="approval-detail-target">{request.entityRef}</dd>
        <dt>{t('approvals.detail.requestedBy')}</dt>
        <dd data-testid="approval-detail-requested-by">{request.requestedBy}</dd>
        <dt>{t('approvals.detail.requestedAt')}</dt>
        <dd data-testid="approval-detail-requested-at">{request.requestedAt}</dd>
        <dt>{t('approvals.detail.requestedReason')}</dt>
        <dd data-testid="approval-detail-requested-reason">{request.requestedReason}</dd>
      </dl>

      <p className="approvals__warning">{t('approvals.selfApprovalNotice')}</p>

      <FormShell
        fields={champsDeDecision}
        submitLabelKey="approvals.submit"
        onSubmit={(valeurs) => onApprove(request.id, valeurs.reason.trim())}
      />
    </div>
  );
}
